import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  FormController,
  FormLabel,
  DrawerBody,
  DrawerFooter,
  SkeletonBlock,
  Typography
} from '@one-thd/sui-atomic-components';
import { InfoFilled } from '@one-thd/sui-icons';
import { useAddToQuote } from '../hooks/useQuotes';
import { DeliveryZipCode } from './DeliveryZipCode';
import { Quotes } from '../shared/Quotes';
import { ProductInfo } from '../shared/ProductInfo';
import { useCustomerInformation } from '../hooks/useCustomerInformation';
import { triggerCloseQuoteAnalytics, triggerErrorAnalytics } from '../../utils/helpers';
import {
  DEFAULT_SELECTED_QUOTE, DELIMITER, DELIVERY_FULFILLMENT, SCREENS, ADD_TO_QUOTE_X_OR_CANCEL_BUTTON
} from '../shared/Constants';

const AddToQuoteFormDrawer = ({
  location,
  onClose,
  products,
  quoteList,
  quotesPageSize,
  selectedQuote,
  setDeliveryZip,
  setError,
  setQuoteName,
  setQuotesPageSize,
  setSelectedQuote,
  showLoadMore,
  projectQuoteData,
  productExceptions,
  setQuoteOriginAnalytic,
  setScreen,
  quoteTimeTrackingRef
}) => {
  const customerInfo = useCustomerInformation();
  const { svocId } = customerInfo;
  const { deliveryZip, localStoreId } = location || {};

  const showZipCode = products?.some((item) => DELIVERY_FULFILLMENT.includes(item?.fulfillmentMethod)
  );
  const [isDisabled, setDisabled] = useState(showZipCode && !deliveryZip);
  const { projectId, projectZipCode } = projectQuoteData || {};

  const { CREATE_NEW_QUOTE, ERROR_SCREEN, SUCCESS } = SCREENS;

  const [addToQuote, quoteResponse] = useAddToQuote({
    deliveryZip,
    items: products,
    localStoreId,
    quoteId: selectedQuote?.quoteId,
    svocId,
    userId: selectedQuote?.userId,
    projectId
  });
  const { loading, data, error } = quoteResponse;

  const addItemToQuote = () => {
    addToQuote();
  };

  const triggerAnalytics = () => {
    const currentRefQuote = quoteTimeTrackingRef?.current;
    const startTime = currentRefQuote?.startTime || 0;
    const elapsedSeconds = Math.floor(Date.now() / 1000) - startTime;
    const combinedProducts = [...products, ...productExceptions];
    window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.quoteAdd', {
      quoteType: 'online',
      quoteId: data?.addToQuote?.cartId || 'n/a',
      quoteTimeElapsed: elapsedSeconds.toString(),
      item: combinedProducts?.map((product) => ({
        quantity: product?.quantity || 0,
        price: {
          basePrice: (product?.pricing?.value && product?.quantity)
            ? String(Number(product?.pricing?.value) * Number(product?.quantity))
            : 0
        },
        productInfo: {
          sku: product?.itemId || 'n/a'
        },
        productException: (product?.flag || 'n/a').toLowerCase()
      }))
    });
  };

  const selectQuote = (evt) => {
    const { value } = evt?.target;
    const [quoteId, userId, quoteName] = value.split(DELIMITER);
    setSelectedQuote({
      userId,
      quoteId,
      quoteName
    });
  };

  const createNewQuoteClick = () => {
    setScreen(CREATE_NEW_QUOTE);
    setSelectedQuote(DEFAULT_SELECTED_QUOTE);
    window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.createAQuoteOverlay');
  };

  const handleClose = () => {
    onClose();
    triggerCloseQuoteAnalytics(ADD_TO_QUOTE_X_OR_CANCEL_BUTTON);
  };

  useEffect(() => {
    if (data && !loading && !error) {
      setQuoteName(selectedQuote?.quoteName);
      setScreen(SUCCESS);
      triggerAnalytics();
      setQuoteOriginAnalytic('add-to-quote');
    } else if (error) {
      triggerErrorAnalytics(error);
      setError(error);
      setScreen(ERROR_SCREEN);
    }
  }, [data, loading, error]);

  if (loading) {
    return (
      <DrawerBody>
        <SkeletonBlock />
      </DrawerBody>
    );
  }

  return (
    <>
      <DrawerBody>
        <ProductInfo products={products} storeId={localStoreId} />

        <div className="sui-my-6 sui-border-b-1 sui-border-primary sui-border-solid" />

        {showZipCode && !projectZipCode && (
          <DeliveryZipCode
            deliveryZip={deliveryZip}
            setDeliveryZip={setDeliveryZip}
            setDisabled={setDisabled}
          />
        )}
        {showZipCode && projectZipCode && (
          <div className="sui-mb-6">
            <Typography>Delivery Zip Code</Typography>
            <Typography weight="bold">{projectZipCode}</Typography>
          </div>
        )}
        <Button
          className="sui-mb-4 sui-mt-6 sui-text-base sui-flex"
          variant="text"
          onClick={createNewQuoteClick}
          data-testid="create-quote"
          disabled={isDisabled}
        >
          <u>Create New Quote</u>
        </Button>
        <div className="sui-pb-4 sui-text-base">-or-</div>
        <FormController>
          <FormLabel id="quote-names" className="sui-cursor-default sui-text-base">
            Add to an Existing Quote:
          </FormLabel>
          <div className="sui-pt-4 sui-pr-2 sui-flex">
            <div className="sui-pr-2 sui-pt-1">
              <InfoFilled color="info" size="small" />
            </div>
            <Typography variant="body-sm" color="info">
              Prices will update when adding items to or changing the ZIP Code of an existing quote
            </Typography>
          </div>
          <Quotes
            quoteList={quoteList}
            selectedQuote={selectedQuote}
            selectQuote={selectQuote}
            showLoadMore={showLoadMore}
            quotesPageSize={quotesPageSize}
            setQuotesPageSize={setQuotesPageSize}
          />
        </FormController>
      </DrawerBody>
      <DrawerFooter>
        <ButtonGroup orientation="vertical">
          <Button
            fullWidth
            variant="primary"
            onClick={addItemToQuote}
            disabled={!selectedQuote?.quoteId?.length || isDisabled}
            data-testid="add-to-existing-quote"
          >
            Add to Quote
          </Button>
          <Button fullWidth variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
        </ButtonGroup>
      </DrawerFooter>
    </>
  );
};

AddToQuoteFormDrawer.propTypes = {
  location: PropTypes.shape({
    deliveryZip: PropTypes.string,
    localStoreId: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      fulfillmentLocation: PropTypes.string.isRequired,
      fulfillmentMethod: PropTypes.string.isRequired,
      identifiers: PropTypes.shape({
        brandName: PropTypes.string,
        productLabel: PropTypes.string
      }).isRequired,
      pricing: PropTypes.shape({
        value: PropTypes.number
      }).isRequired,
      media: PropTypes.shape({
        images: PropTypes.arrayOf(
          PropTypes.shape({
            altText: PropTypes.string,
            sizes: PropTypes.arrayOf(PropTypes.string),
            subType: PropTypes.string,
            type: PropTypes.string,
            url: PropTypes.string
          })
        )
      })
    })
  ).isRequired,
  quoteList: PropTypes.arrayOf(
    PropTypes.shape({
      quoteId: PropTypes.string,
      quoteName: PropTypes.string,
      userId: PropTypes.string
    })
  ).isRequired,
  quotesPageSize: PropTypes.number.isRequired,
  selectedQuote: PropTypes.shape({
    quoteId: PropTypes.string,
    quoteName: PropTypes.string,
    userId: PropTypes.string
  }).isRequired,
  setDeliveryZip: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  setQuoteName: PropTypes.func.isRequired,
  setQuotesPageSize: PropTypes.func.isRequired,
  setSelectedQuote: PropTypes.func.isRequired,
  setScreen: PropTypes.func.isRequired,
  showLoadMore: PropTypes.bool.isRequired,
  projectQuoteData: PropTypes.shape({
    projectId: PropTypes.string,
    projectZipCode: PropTypes.string,
    projectName: PropTypes.string
  }),
  productExceptions: PropTypes.arrayOf(
    PropTypes.shape({
      flag: PropTypes.string.isRequired,
      fulfillment: PropTypes.shape({}),
      identifiers: PropTypes.shape({}),
      pricing: PropTypes.shape({}),
      itemId: PropTypes.string,
      id: PropTypes.string,
      quantity: PropTypes.number
    })
  ),
  setQuoteOriginAnalytic: PropTypes.func.isRequired,
  quoteTimeTrackingRef: PropTypes.shape({
    current: PropTypes.shape({
      startTime: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])])
    })
  }).isRequired
};

AddToQuoteFormDrawer.defaultProps = {
  projectQuoteData: {},
  productExceptions: []
};

AddToQuoteFormDrawer.displayName = 'AddToQuoteFormDrawer';

export { AddToQuoteFormDrawer };
